import type { AccountResult } from '../../api/types.js';

/**
 * Where a ⌘K result lands (FR-050, US5 scenario 3).
 *
 * The destination is that client's **Region Detail, with the client pre-selected in the
 * breakdown and the chart already scoped to it**. Landing on the region alone would leave the
 * user to find the client again in a table of a hundred and fifty rows, which is the work the
 * palette exists to remove.
 *
 * The client rides in the query string rather than in component state so the destination is
 * linkable and survives a reload — and so Region Detail has exactly one source of truth for
 * the selection, whether it arrived from ⌘K or from a row click.
 *
 * A client operating in more than one region yields **one result per region** (FR-050), so the
 * choice of regional context is made in the results list, never guessed here.
 */

export function hrefFor(result: AccountResult): string {
  // The server already computes this, and echoing it keeps one definition. The local
  // construction below is the fallback for a result that predates the field.
  if (result.href) return result.href;
  return `/regions/${encodeURIComponent(result.regionCode)}?client=${encodeURIComponent(
    result.clientKey,
  )}`;
}

export interface ResultGroup {
  clientName: string;
  results: AccountResult[];
}

/**
 * Groups results by client so a multi-region client reads as one client in two places rather
 * than as two unrelated rows that happen to share a name.
 */
export function groupByClient(results: AccountResult[]): ResultGroup[] {
  const groups = new Map<string, ResultGroup>();

  for (const result of results) {
    const existing = groups.get(result.clientKey);
    if (existing) existing.results.push(result);
    else groups.set(result.clientKey, { clientName: result.clientName, results: [result] });
  }

  return [...groups.values()];
}

/** Announcement text for a result row, so the region is never implicit. */
export function describeResult(result: AccountResult): string {
  return `${result.clientName} in ${result.regionName} (${result.regionCode})`;
}
